import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import type { SystemUserWithEmployeeData } from '@/shared/hooks/useSystemUsers';

interface UserCacheState {
  /** Map of userId (by multiple keys) to display name */
  userCache: Record<string, string>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

interface UserCacheActions {
  /** Start subscription to system_users. Ref-counted; returns unsubscribe. */
  subscribe: () => () => void;
  reset: () => void;
}

type UserCacheStore = UserCacheState & UserCacheActions;

// Ref-counted subscription management
let refCount = 0;
let unsub: (() => void) | null = null;

const initialState: UserCacheState = {
  userCache: {},
  loading: true,
  loaded: false,
  error: null,
};

export const useUserCacheStore = create<UserCacheStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      subscribe: () => {
        refCount++;

        if (refCount === 1) {
          if (!get().loaded) {
            set({ loading: true });
          }

          const q = query(collection(db, COLLECTION_NAMES.APP_USERS));

          unsub = onSnapshot(
            q,
            (snapshot) => {
              try {
                const cache: Record<string, string> = {};

                snapshot.docs.forEach((doc) => {
                  const userData = doc.data() as SystemUserWithEmployeeData;
                  const userName =
                    userData.employee_data?.name ||
                    userData.email ||
                    'Unknown User';

                  // Cache by firebase_uid (primary identifier)
                  if (userData.firebase_uid) {
                    cache[userData.firebase_uid] = userName;
                  }

                  // Cache by employee_id (common identifier for audit trails)
                  if (userData.employee_id) {
                    cache[userData.employee_id.toString()] = userName;
                  }

                  // Cache by document ID
                  cache[doc.id] = userName;

                  // Cache by email
                  if (userData.email) {
                    cache[userData.email] = userName;
                  }
                });

                // Special placeholders
                cache['Current User'] = 'Current User';
                cache['current_user'] = 'Current User';

                set({ userCache: cache, loaded: true, loading: false, error: null });
              } catch (err: any) {
                set({
                  error: err?.message || 'Failed to build user cache',
                  loading: false,
                });
              }
            },
            (err) => {
              set({ error: err.message, loading: false });
            },
          );
        }

        return () => {
          refCount--;
          if (refCount === 0) {
            unsub?.();
            unsub = null;
          }
        };
      },

      reset: () => {
        if (unsub) {
          unsub();
          unsub = null;
          refCount = 0;
        }
        set(initialState);
      },
    }),
    { name: 'UserCacheStore' },
  ),
);
